import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import TabContentWrapper from '../TabContentWrapper';
import MediaDescription from '../../details/MediaDescription';
import {EmptyState, TscButtonType, TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import EditDetailsModal from '../../details/EditDetailsModal';
import config from '../../../service/config';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {mediaStore} from '../../../context/mediaProvider';
import cssConstants from '../../../constants/cssConstants';
import {MediaDetailsLink} from '../../details/MediaDetailsLink';
import HorizontalSeparator from '../../util/HorizontalSeparator';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import MediaDetails from '../../details/MediaDetails';

dayjs.extend(relativeTime);

const EmptyStateWrapper = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   width: 100%;
   margin: 3rem 0;
`;

const BaseTabsWrapper = styled.div`
   width: 100%;
   display: flex;
   margin: 0 auto;
   box-sizing: border-box;
`;

const TabsBarWrapper = styled(BaseTabsWrapper)<{theme: TscThemeNames; hasDescription: boolean}>`
   padding: 16px 12px;
   position: relative;
   font-size: .875rem;
   font-weight: ${cssConstants.semiBoldWeight};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   max-width: ${cssConstants.contentWidth};
   border-radius: ${props => props.hasDescription ? ` ${TscStyles.border.radius.xl} ${TscStyles.border.radius.xl} 0 0` : TscStyles.border.radius.xl};
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};

   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px)  {
      border-radius: 0;
      padding: 8px 12px 16px;
   }

   &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: calc(100% - 24px);
      border-bottom: ${props => props.hasDescription ? `2px solid ${props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.medium}` : 'none'};
   }
`;

export const MediaDetailsSpan = styled.span<{theme: TscThemeNames}>`
   white-space: pre;
`;

const Card = styled.div<{theme: TscThemeNames}>`
   position: relative;
   max-width: ${cssConstants.contentWidth};
   width: 100%;
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   border-radius: 0 0 ${TscStyles.border.radius.xl} ${TscStyles.border.radius.xl};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   
   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px)  {
      border-radius: 0;
   }
`;

export const TabsContentWrapper = styled(BaseTabsWrapper)`
   flex-direction: column;
   justify-content: center;
   align-items: center;
   max-width: ${cssConstants.contentWidth};
   z-index: ${cssConstants.zIndex.viewPageElement};
`;

const TabsPadding = styled.div`
   flex-grow: 1;
`;

const DescriptionWrapper = styled.div`
   margin: 0 12px;
`;

const ShadowDiv = styled.div<{theme: TscThemeNames}>`
   border-radius: ${TscStyles.border.radius.xl};
   max-width: 64rem;
   margin: 0px auto;
   width: 100%;
      
   @media screen and (min-width: ${cssConstants.mediaTabsDisplayBreakpoint}px)  {
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15), 0 0 2px rgba(0, 0, 0, 0.1);
   }
`;

export const DetailsTabBase: React.FC<IStateMappedProps> = ({IsYouTube, Author, YouTubeAuthor, YouTubeUrl, SharedDateTime, ViewCount, Description, IsReady}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [showEditDetailsModal, setShowEditDetailsModal] = React.useState(false);
   const [showDetails, setShowDetails] = useState(window.innerWidth <= cssConstants.mediaTabsDisplayBreakpoint);

   useEffect(() => {
      window.addEventListener('resize', handleResize);
      return () => {
         window.removeEventListener('resize', handleResize);
      };
   }, []);

   const handleResize = () => {
      setShowDetails(window.innerWidth <= cssConstants.mediaTabsDisplayBreakpoint);
   };

   const emptyState = (
      <EmptyStateWrapper>
         <EmptyState
            title={t('details.tabEmptyState.title')}
            text={t('details.tabEmptyState.text')}
            primaryButtonText={t('details.tabEmptyState.buttonText')}
            primaryButtonType={TscButtonType.secondary}
            onPrimaryClick={IsReady && (config.watchConfig.isOwnerAdminOrTeamEditor) ? () => setShowEditDetailsModal(true) : null}
            themeName={theme}
            maxWidth="100%"
            testId="details-tab-empty-state"
         />
      </EmptyStateWrapper>
   );

   const nonEmptyState = <MediaDescription rawDescriptionText={Description} />;

   return (
      <>
         {showDetails && <MediaDetails showMediaLocation={false} />}
         <ShadowDiv data-test-id="details-tab">
            <TabsBarWrapper
               theme={theme}
               hasDescription={!!Description || config.watchConfig.isOwnerAdminOrTeamEditor}
            >
               <MediaDetailsSpan>
                  {t('AddedBy_by')}
                  {' '}
               </MediaDetailsSpan>
               <MediaDetailsSpan theme={theme}>
                  {Author}
               </MediaDetailsSpan>
               {IsYouTube && (
                  <>
                     <HorizontalSeparator />
                     {t('CreatedByYoutubeUser')}
                     <MediaDetailsLink
                        href={YouTubeUrl}
                        theme={theme}
                     >
                        {YouTubeAuthor}
                     </MediaDetailsLink>
                  </>
               )}
               <HorizontalSeparator />
               <MediaDetailsSpan title={dayjs(SharedDateTime).toLocaleString()}>
                  {t('AddedBy_Added')}
                  {' '}
                  {dayjs(SharedDateTime).fromNow()}
               </MediaDetailsSpan>
               <HorizontalSeparator />
               <MediaDetailsSpan>
                  {ViewCount}
                  {' '}
                  {t('Views')}
               </MediaDetailsSpan>
            </TabsBarWrapper>
            {showDetails ? (
               <TabContentWrapper
                  showSpinner={false}
                  showError={false}
               >
                  {Description ? nonEmptyState : (config.watchConfig.isOwnerAdminOrTeamEditor && emptyState)}
               </TabContentWrapper>
            )
               : (
                  <TabsPadding
                     id="tabs-content-wrapper"
                     data-test-id="tabs-content-wrapper"
                  >
                     <TabsContentWrapper>
                        <Card theme={theme}>
                           {Description ? (
                              <DescriptionWrapper>
                                 {nonEmptyState}
                              </DescriptionWrapper>
                           )
                              : (config.watchConfig.isOwnerAdminOrTeamEditor && (
                                 <DescriptionWrapper>
                                    {emptyState}
                                 </DescriptionWrapper>
                              ))}

                        </Card>
                     </TabsContentWrapper>
                  </TabsPadding>
               )}
         </ShadowDiv>
         {showEditDetailsModal && <EditDetailsModal onClose={() => setShowEditDetailsModal(false)} />}
      </>
   );
};

export interface IStateMappedProps {
   IsYouTube: boolean;
   Author: string;
   YouTubeAuthor: string;
   YouTubeUrl: string;
   SharedDateTime: Date;
   ViewCount: number;
   Description: string;
   IsReady: boolean;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {IsYouTube, Author, YouTubeAuthor, YouTubeUrl, SharedDateTime, ViewCount, Description, IsReady}: Partial<IStateMappedProps>
): IStateMappedProps => ({IsYouTube, Author, YouTubeAuthor, YouTubeUrl, SharedDateTime, ViewCount, Description, IsReady});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(DetailsTabBase);
